<script setup>
import { computed, inject } from 'vue';

const props = defineProps({
  index: {
    type: Number,
    default: 0,
  },
  name: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  count: {
    type: Number,
    default: 0,
  },
  unreadCount: {
    type: Number,
    default: 0,
  },
  showBadge: {
    type: Boolean,
    default: true,
  },
  isCompact: {
    type: Boolean,
    default: false,
  },
  onChannelSurface: {
    type: Boolean,
    default: false,
  },
  accentColor: {
    type: String,
    default: '',
  },
});

const activeIndex = inject('activeIndex');
const updateActiveIndex = inject('updateActiveIndex');

const active = computed(() => props.index === activeIndex.value);
const getItemCount = computed(() => props.count);
const getUnreadCount = computed(() => {
  if (!props.unreadCount) return '';
  return props.unreadCount < 100 ? `${props.unreadCount}` : '99+';
});

const columnStyle = computed(() => {
  if (!props.onChannelSurface || !active.value || !props.accentColor) return {};
  return {
    backgroundColor: `color-mix(in srgb, ${props.accentColor} 38%, #d6d6d6)`,
  };
});

const onTabClick = event => {
  event.preventDefault();
  if (!props.disabled) {
    updateActiveIndex(props.index);
  }
};
</script>

<template>
  <li
    class="flex-1 min-w-0 my-0"
    :class="[
      isCompact ? 'mx-0' : 'mx-2 ltr:first:ml-0 rtl:first:mr-0 ltr:last:mr-0 rtl:last:ml-0',
      onChannelSurface
        ? 'rounded-2xl bg-n-slate-4/90 dark:bg-n-solid-3'
        : 'hover:text-n-slate-12',
    ]"
    :style="columnStyle"
  >
    <a
      v-if="onChannelSurface"
      class="flex flex-col items-center justify-center gap-1 py-1.5 px-1 select-none cursor-pointer relative text-button min-h-[3.5rem]"
      @click="onTabClick"
    >
      <div
        class="relative rounded-full grid place-items-center bg-n-background text-n-slate-11 shadow-sm"
        style="width: 1.5rem; height: 1.5rem;"
      >
        <i v-if="icon" :class="icon" class="text-[13px] leading-none" />
        <span
          v-if="getUnreadCount"
          class="absolute -top-0.5 min-h-[14px] min-w-[14px] px-0.5 bg-n-ruby-9 rounded-full grid place-items-center text-[8px] leading-none text-white font-semibold"
          style="right: -0.45rem;"
        >
          {{ getUnreadCount }}
        </span>
      </div>
      <span
        class="flex items-center justify-center gap-0.5 max-w-full text-[10px] uppercase tracking-wide leading-none"
        :class="active ? 'text-n-slate-12 font-semibold' : 'text-n-slate-11'"
      >
        <span class="truncate">{{ name }}</span>
        <span class="shrink-0 tabular-nums">({{ getItemCount }})</span>
      </span>
    </a>
    <a
      v-else
      class="flex items-center justify-center flex-row select-none cursor-pointer relative text-button h-full gap-0.5"
      :class="[
        isCompact ? 'py-1.5 px-1 text-xs' : '!text-base py-3',
        [
          'after:absolute after:bottom-px after:left-1 after:right-1 after:h-[2px] after:rounded-full after:transition-all after:duration-200',
          active
            ? 'text-n-blue-11 after:bg-n-brand after:opacity-100'
            : 'text-n-slate-11 after:bg-transparent after:opacity-0',
        ],
      ]"
      @click="onTabClick"
    >
      <i v-if="icon" :class="icon" class="text-sm shrink-0" />
      <span v-if="name" class="truncate">{{ name }}</span>
      <div
        v-if="showBadge"
        class="relative rounded-full flex items-center justify-center text-[10px] font-medium shrink-0"
        :class="[
          isCompact
            ? 'h-4 min-w-[16px] px-1'
            : 'h-5 min-w-[20px] px-1.5 my-0 ltr:ml-1 rtl:mr-1 py-0',
          active
            ? 'bg-n-blue-3 text-n-blue-11'
            : 'bg-n-alpha-1 text-n-slate-10',
        ]"
      >
        <span>{{ getItemCount }}</span>
        <span
          v-if="getUnreadCount"
          class="absolute -top-1 ltr:-right-1 rtl:-left-1 min-h-[12px] min-w-[12px] px-0.5 bg-n-ruby-9 rounded-full grid place-items-center text-[8px] leading-none text-white font-semibold"
        >
          {{ getUnreadCount }}
        </span>
      </div>
    </a>
  </li>
</template>
